import { useObserver } from 'mobx-react-lite'

import { useFlag } from '@src/app/feature-flags'
import { useService } from '@src/service/context'

import { useParagon } from './useParagon'

// These are integrations whose titles are prefixed with "custom." in the Paragon API
export const paragonCustomIntegrations = ['jobber']

// This is how it was referenced in Paragon. Keep the name for now, but once Paragon
// has been ripped out, we can just call this 'jobber'.
const jobberIntegrationName = 'custom.jobber'

const integrationsWithCustomConfig = ['hubspot', jobberIntegrationName]

interface IntegrationsResult {
  reenable: string[]
  enabled: string[]
  available: string[]
  legacy: string[]
  withCustomConfig: string[]
}

export function useIntegrations(): IntegrationsResult {
  const hasParagonMigrationStarted = useFlag('webParagonIntegrationsMigration')
  const migratedIntegrationsFlag = useFlag('webParagonIntegrationsLegacyToParagon')
  const enableJobberIntegration = useFlag('webParagonIntegrationsEnableJobberIntegration')
  const enableNativeJobberIntegration = useFlag('webEnableNativeJobberIntegration')
  const migratedIntegrations = hasParagonMigrationStarted ? migratedIntegrationsFlag : []
  const { integrations, getAllMetadata } = useParagon()
  const service = useService()

  const legacyIntegrations = [
    ...(enableNativeJobberIntegration ? [jobberIntegrationName] : []),
    'gong',
    'hubspot',
    'salesforce',
    'slack',
    'make',
    'zapier',
  ]

  const allParagonMetadata = enableJobberIntegration
    ? getAllMetadata()
    : getAllMetadata().filter((metadata) => metadata.type !== jobberIntegrationName)

  const withParagonData = allParagonMetadata.map((metadata) => ({
    type: metadata.type,
    metadata: metadata,
    data: integrations[metadata.type],
  }))

  const legacyEnabled = useObserver(() =>
    legacyIntegrations.filter((integration) =>
      service.integration.isConnectedByName(integration.replace(/^custom\./, '')),
    ),
  )

  const reenable = legacyEnabled.filter((integration) =>
    migratedIntegrations.includes(integration),
  )

  // If they already had Jobber connected via Paragon, we want to tell them to
  // re-enable the integration so they can connect via our native integration.
  if (
    withParagonData.find((d) => d.type === jobberIntegrationName) &&
    enableNativeJobberIntegration &&
    !legacyEnabled.includes(jobberIntegrationName)
  ) {
    reenable.push(jobberIntegrationName)
  }

  const enabledSet = new Set(legacyEnabled)

  for (const { type, data } of withParagonData) {
    if (data?.enabled) {
      enabledSet.add(type)
    }
  }

  const availableSet = new Set([
    ...allParagonMetadata.map((metadata) => metadata.type),
    ...legacyIntegrations,
  ])

  for (const enabledIntegration of enabledSet) {
    availableSet.delete(enabledIntegration)
  }

  const actualLegacy = legacyIntegrations.filter(
    (integration) => !migratedIntegrations.includes(integration),
  )

  return {
    reenable,
    enabled: Array.from(enabledSet),
    available: Array.from(availableSet),
    legacy: actualLegacy,
    withCustomConfig: integrationsWithCustomConfig,
  }
}
